<template>
  <div class="usage-dashboard-table">
    <div class="usage-dashboard-table__header">
      <span class="usage-dashboard-table__title heading-s">Courses</span>
      <div class="usage-dashboard-table__search">
        <eg-input
          v-model="searchQuery"
          placeholder="Search courses"
          variant="search"
          type="search"
          size="medium"
        />
      </div>
    </div>

    <div class="usage-dashboard-table__wrapper">
      <eg-pv-table
        :items="rows"
        :fields="tableFields"
        :loading="isLoading"
        empty-text="No courses match these filters."
        class="usage-dashboard-table__table"
        @sort="handleSort"
      >
        <template #cell(name)="{ item }">
          <div class="usage-dashboard-table__course-cell">
            <span class="usage-dashboard-table__course-name paragraph-s">{{
              item.name
            }}</span>
            <span class="usage-dashboard-table__unit">{{
              assignmentsCountLabel(item.assignmentsCount)
            }}</span>
          </div>
        </template>

        <template #cell(instructor)="{ item }">
          <div class="usage-dashboard-table__instructor">
            <eg-avatar :name="instructorName(item.instructor)" :size="28" />
            <span class="paragraph-s">{{
              instructorName(item.instructor)
            }}</span>
          </div>
        </template>

        <template #cell(submissionsGraded)="{ value }">
          <div class="usage-dashboard-table__stacked-cell">
            <span class="paragraph-s usage-dashboard-table__value">{{
              value.toLocaleString()
            }}</span>
            <span class="usage-dashboard-table__unit">submissions</span>
          </div>
        </template>

        <template #cell(averageGrade)="{ value }">
          <span
            class="paragraph-s usage-dashboard-table__value usage-dashboard-table__value--grade"
          >
            {{ value != null ? value + "%" : "—" }}
          </span>
        </template>

        <template #cell(timeSavedHours)="{ value }">
          <span
            class="paragraph-s usage-dashboard-table__value usage-dashboard-table__value--saved"
          >
            {{ value }} h
          </span>
        </template>

        <template #cell(lastLoginAt)="{ value }">
          <span class="paragraph-s usage-dashboard-table__cell-end">{{
            formatLastLogin(value)
          }}</span>
        </template>
      </eg-pv-table>
    </div>

    <metrics-pagination
      :value="page"
      :total-rows="total"
      :per-page="limit"
      @change="onPageChange"
    >
      <template #label>
        {{ showingLabel }}
      </template>
    </metrics-pagination>
  </div>
</template>

<script>
import EgAvatar from "@/components/global/eg/eg_avatar.vue";
import MetricsPagination from "@/views/metrics/components/shared/metrics_pagination.vue";
import { getUsageDashboardCourses } from "@/api/requests/usageDashboard.js";
import debounce from "@/utils/debounce.js";

export default {
  name: "UsageDashboardTable",

  components: {
    EgAvatar,
    MetricsPagination,
  },

  props: {
    orgId: {
      type: String,
      required: true,
    },
    courseId: {
      type: Number,
      default: null,
    },
    instructorId: {
      type: Number,
      default: null,
    },
    dateFrom: {
      type: String,
      default: null,
    },
    dateTo: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      limit: 10,
      searchQuery: "",
      sortField: "submissionsGraded",
      sortOrder: "desc",
      isLoading: false,
    };
  },

  computed: {
    tableFields() {
      return [
        { key: "name", label: "Course", sortable: true },
        {
          key: "instructor",
          label: "Instructor",
          sortable: true,
          style: "width: 20%",
        },
        {
          key: "submissionsGraded",
          label: "Graded",
          sortable: true,
          style: "width: 12%",
          align: "right",
        },
        {
          key: "averageGrade",
          label: "Avg grade",
          sortable: true,
          style: "width: 12%",
          align: "right",
        },
        {
          key: "timeSavedHours",
          label: "Time saved",
          sortable: true,
          style: "width: 12%",
          align: "right",
        },
        {
          key: "lastLoginAt",
          label: "Last login",
          sortable: true,
          style: "width: 14%",
          align: "right",
        },
      ];
    },

    showingLabel() {
      return `${this.rows.length} of ${this.total} courses`;
    },

    filtersKey() {
      return [
        this.courseId,
        this.instructorId,
        this.dateFrom,
        this.dateTo,
      ].join("_");
    },
  },

  watch: {
    filtersKey() {
      this.page = 1;
      this.fetchRows();
    },

    searchQuery() {
      this.page = 1;
      this.emitQuery();
      this.debouncedFetch();
    },
  },

  created() {
    this.debouncedFetch = debounce(this.fetchRows, 400);
    this.fetchRows();
  },

  methods: {
    instructorName(instructor) {
      if (!instructor) return "—";
      return `${instructor.firstName || ""} ${
        instructor.lastName || ""
      }`.trim();
    },

    formatLastLogin(value) {
      if (!value) return "—";
      const days = Math.floor(
        (Date.now() - new Date(value).getTime()) / 86400000
      );
      if (days <= 0) return "Today";
      if (days === 1) return "Yesterday";
      return `${days} days ago`;
    },

    assignmentsCountLabel(count) {
      const n = count || 0;
      return `${n} assignment${n === 1 ? "" : "s"}`;
    },

    handleSort({ field, order }) {
      this.sortField = field;
      this.sortOrder = order;
      this.page = 1;
      this.emitQuery();
      this.fetchRows();
    },

    onPageChange(page) {
      this.page = page;
      this.fetchRows();
    },

    emitQuery() {
      this.$emit("query-change", {
        search: this.searchQuery.trim() || undefined,
        sortBy: this.sortField,
        sortDir: this.sortOrder,
      });
    },

    async fetchRows() {
      this.isLoading = true;
      try {
        const res = await getUsageDashboardCourses({
          orgId: this.orgId,
          courseId: this.courseId,
          instructorId: this.instructorId,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          search: this.searchQuery.trim() || undefined,
          sortBy: this.sortField,
          sortDir: this.sortOrder,
          page: this.page,
          limit: this.limit,
        });
        this.rows = res.data?.data || [];
        this.total = res.data?.total || 0;
      } catch {
        this.rows = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../metrics/components/shared/metrics_card";

.usage-dashboard-table {
  border-radius: 12px;
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-background, #fff);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 16px;
  }

  &__title {
    font-size: var(--typography-font-size-fs-600);
  }

  &__search {
    width: 220px;
  }

  &__wrapper {
    overflow-x: auto;
    padding: 0 24px;
  }

  &__course-cell {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__course-name {
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-on-white);
  }

  &__instructor {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__stacked-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
  }

  &__value {
    display: block;
    text-align: right;
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white);

    &--grade {
      color: var(--color-text-primary-default, #7c3aed);
    }

    &--saved {
      color: var(--color-text-semantic-onSuccess, #308c4a);
    }
  }

  &__cell-end {
    display: block;
    text-align: right;
  }

  &__unit {
    font-size: var(--typography-font-size-fs-200, 12px);
    color: var(--color-text-neutral-fade);
  }
}
</style>
